<script setup lang="ts">
import { computed, ref, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import { EMIT } from '@/enum';
import { useMessageCenterStore } from '@/store';
import PDFViewer from '@/components/PDFViewer/PDFViewer.vue';
import download from 'downloadjs';
import { FileInterface } from '@/types';

interface Normalized {
  name: string;
  type: string;
  blob: Blob;
}

interface PropsInterface {
  title: string;
  visible?: boolean;
  file?: Blob | File | FileInterface;
}

const props = withDefaults(defineProps<PropsInterface>(), {
  visible: false,
  file: undefined,
});

const emit = defineEmits<{
  (event: EMIT.CANCEL): void;
  (event: 'download'): void;
  (event: 'update:visible', value: boolean): void;
}>();

const messageCenterStore = useMessageCenterStore();
const { attachmentFile } = storeToRefs(messageCenterStore);

const fallbackTitle = ref('File Preview');

const normalizedFile = computed<Normalized>(() => {
  if (props.file instanceof Blob) {
    const f = props.file as File | Blob;
    const name = (f as File)?.name;
    const type = f.type || 'application/octet-stream';
    return { name, type, blob: f };
  }

  if (props.file && !(props.file instanceof Blob)) {
    const fi = props.file as FileInterface;
    const type = fi.type || 'application/octet-stream';
    const blob = new Blob([fi.content as ArrayBuffer | Uint8Array], { type });
    return { name: fi.name, type, blob };
  }

  if (attachmentFile.value) {
    const type = attachmentFile.value.type || 'application/octet-stream';
    const blob = new Blob([attachmentFile.value.content], { type });
    return { name: attachmentFile.value.name, type, blob };
  }

  return null;
});

let objectUrl: string | null = null;

onUnmounted(() => {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  attachmentFile.value = undefined;
});

const blobUrl = computed(() => {
  if (!normalizedFile.value) return '';
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = URL.createObjectURL(normalizedFile.value.blob);
  return objectUrl;
});

const fileExtension = computed(() => {
  const name = normalizedFile.value?.name?.toLowerCase() || '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.substring(dot) : '';
});

const isImage = computed(() => normalizedFile.value?.type.startsWith('image/'));
const isAudio = computed(() => normalizedFile.value?.type.startsWith('audio/'));
const isVideo = computed(() => normalizedFile.value?.type.startsWith('video/'));
const isPDF = computed(() => normalizedFile.value?.type === 'application/pdf');
const isOther = computed(() => !isImage.value && !isAudio.value && !isVideo.value && !isPDF.value);

const onCancel = () => {
  emit('update:visible', false);
  emit(EMIT.CANCEL);
};

const onDownload = () => {
  if (!normalizedFile.value) return;
  const { blob, name, type } = normalizedFile.value;
  download(blob, name, type || 'application/octet-stream');
};

const onUpdateVisible = (value: boolean) => {
  if (!value) onCancel();
};
</script>

<template>
  <Dialog
    :visible="visible"
    :header="normalizedFile?.name || title || fallbackTitle"
    :modal="true"
    :draggable="false"
    :class="isOther || isAudio ? 'w-6 preview-file__modal' : 'w-11 preview-file__modal'"
    content-class="p-0 bg-white overflow-x-hidden"
    @hide="onCancel"
    @update:visible="onUpdateVisible"
  >
    <template #header>
      <div class="flex justify-content-between align-items-center">
        <h4 class="title">{{ normalizedFile?.name || title || fallbackTitle }}</h4>

        <AppButton
          v-if="normalizedFile && !isOther && !file"
          v-tooltip="'Download file'"
          type="secondary"
          icon-right="system_update_alt"
          @click="onDownload"
        />
      </div>
    </template>

    <div v-if="normalizedFile" class="preview-file__wrapper">
      <!-- Image -->
      <img v-if="isImage" :src="blobUrl" :alt="normalizedFile.name" class="preview-image" />

      <!-- Audio -->
      <audio v-else-if="isAudio" controls :src="blobUrl" class="preview-audio" />

      <!-- Video -->
      <video v-else-if="isVideo" controls :src="blobUrl" class="preview-video" />
      <!-- PDF -->
      <PDFViewer
        v-else-if="isPDF"
        :name="normalizedFile.name"
        :src="blobUrl"
        class="preview-pdf"
        @download="onDownload"
      />

      <!-- Other -->
      <div v-else class="preview-file__icon">
        <i class="pi pi-file"></i>
        <p class="file-name">{{ normalizedFile.name }}</p>
        <p class="file-meta">Type: {{ fileExtension }}</p>

        <AppButton
          v-if="!props.file"
          text="Download file"
          type="secondary"
          icon-right="system_update_alt"
          @click="onDownload"
        />
      </div>
    </div>

    <div v-else class="p-3">There is no file to preview.</div>
  </Dialog>
</template>

<style lang="scss" scoped>
:deep {
  .p-dialog {
    width: 100% !important;
    height: auto !important;
    overflow: hidden !important;
  }
}

.preview-file__modal {
  .p-dialog-header {
    div {
      h4 {
        font-size: 1rem;
        font-weight: 700;
        margin-right: 2rem;
      }
      margin: 0.5rem;
    }
  }

  .dialog__container {
    width: 100%;
    z-index: 100000000;
  }

  .preview-file__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background-color: #f9f9f9;

    .preview-image {
      max-width: 90%;
      object-fit: contain;
    }

    .preview-audio {
      width: 100%;
      height: 50px;
      margin-top: 1rem;
    }

    .preview-video,
    .preview-pdf {
      width: 90%;
      height: 90%;
    }

    .preview-file__icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 2rem;

      .file-icon {
        font-size: 48px;
      }

      .file-name {
        margin-top: 0.5rem;
        font-weight: bold;
        word-break: break-word;
      }
    }

    .file-meta {
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
      color: #666;
    }
  }
}
</style>
